import { SlotCell, SlotStatus } from '../../../types/Exams';

export const toLocalParts = (d: Date, timeZone: string) => {
  const f = new Intl.DateTimeFormat('sv-SE', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const [date, time] = f.format(d).split(' ');
  return { date, time }; // date: "2025-09-03", time: "09:30"
};
export const formatDate = (isoYmd: string) =>
  new Date(isoYmd).toLocaleDateString('uk-UA', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  });
export const toYMD = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const isWorkday = (d: Date) => {
  const wd = d.getDay();
  return wd >= 1 && wd <= 5; // Mon..Fri
};
export const normalizeYearMonth = (baseYear: number, m: number) => {
  let year = baseYear;
  let month = m;
  while (month < 1) {
    month += 12;
    year -= 1;
  }
  while (month > 12) {
    month -= 12;
    year += 1;
  }
  return { year, month };
};
export const parseHHMM = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

export const pad = (n: number) => String(n).padStart(2, '0');

export const minutesToHHMM = (m: number) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;

export const addMinutes = (d: Date, m: number) => new Date(d.getTime() + m * 60_000);

// Create all slot start times (in local minutes since 00:00) between [start..end]
export const makeLocalSlotStarts = (startHHMM: string, endHHMM: string, stepMin: number) => {
  const start = parseHHMM(startHHMM);
  const end = parseHHMM(endHHMM);
  const out: number[] = [];
  for (let t = start; t <= end; t += stepMin) out.push(t);
  return out;
};

// Get a local Date at given local date (YYYY-MM-DD) and local HH:mm in a TZ
export const localDateTime = (date: string, hhmm: string, timeZone: string) => {
  // Build a string like "2025-09-03T09:30:00" and let the TZ offset be applied by interpreting as local:
  // We’ll derive the UTC epoch by formatting to parts in that TZ.
  // A simple trick: use Date with the parts and correct via offset math:
  const [Y, M, D] = date.split('-').map(Number);
  const [h, m] = hhmm.split(':').map(Number);
  // Create a Date as if it's UTC, then adjust to real UTC by comparing the intended local wall time in that TZ.
  // Easiest robust way without libs: find UTC by binary search; but we can approximate using the TZ formatter:
  // We'll format a candidate UTC date and adjust minutes difference until the local parts match.
  let guess = new Date(Date.UTC(Y, (M ?? 1) - 1, D ?? 1, h ?? 0, m ?? 0));
  // Quick refine loop (max a few steps due to DST edges)
  for (let i = 0; i < 4; i++) {
    const parts = toLocalParts(guess, timeZone);
    const [y, mo, d] = parts.date.split('-').map(Number);
    const [hh, mm] = parts.time.split(':').map(Number);
    const delta =
      (Y - y) * 24 * 60 +
      (M - mo) * 31 * 24 * 60 + // over-correct but loop will converge
      (D - d) * 24 * 60 +
      (h - hh) * 60 +
      (m - mm);
    if (delta === 0) break;
    guess = addMinutes(guess, delta);
  }
  return guess;
};
export function cellToPeriod(cell: SlotCell, stepMin = 15) {
  // Combine into an ISO string (local time by default).
  // If you want to force UTC, add "Z" at the end.
  const start = new Date(`${cell.date}T${cell.time}:00`);

  // Add stepMin minutes
  const end = new Date(start.getTime() + stepMin * 60_000);

  return { start, end };
}

// export const markCellsInRange = (
//   cells: SlotCell[],
//   startISO: string,
//   endISO: string,
//   status: SlotStatus,
//   by?: string,
//   extra?: Partial<SlotCell>,
// ) => {
//   const start = new Date(startISO).getTime();
//   const end = new Date(endISO).getTime();

//   return cells.map((c) => {
//     const t = new Date(`${c.date}T${c.time}:00`).getTime();

//     // treat a cell as occupied if its start is within [start, end)
//     if (t >= start && t < end) {
//       return { ...c, status, by, id: extra?.id };
//     }
//     return c;
//   });
// };
export const markCellsInRange = (
  cells: SlotCell[],
  startISO: string,
  endISO: string,
  status: SlotStatus,
  by?: string,
  extra?: Partial<SlotCell>, // e.g. { periodId: number } or { id: number }
) => {
  const start = new Date(startISO);
  const end = new Date(endISO);
  const startMs = start.getTime();
  const endMs = end.getTime();

  let matched = false;

  // update any existing cells whose start falls in [start, end)
  const next = cells.map((c) => {
    const tMs = new Date(`${c.date}T${c.time}:00`).getTime();
    if (tMs >= startMs && tMs < endMs) {
      matched = true;
      return {
        ...c,
        status,
        by,
        // keep your chosen id field; here I assume you store period id in `id`
        id: extra?.id ?? (status === 'available' ? undefined : c.id),
        ...extra,
      };
    }
    return c;
  });

  // if no existing cell matched, INSERT one for the slot start
  if (!matched) {
    const y = start.getFullYear();
    const m = pad(start.getMonth() + 1);
    const d = pad(start.getDate());
    const hh = pad(start.getHours());
    const mm = pad(start.getMinutes());
    next.push({
      date: `${y}-${m}-${d}`,
      time: `${hh}:${mm}`,
      status,
      by,
      id: extra?.id, // may be undefined for the pure-optimistic pass
      note: (extra as any)?.note,
    } as SlotCell);
  }

  return next;
};